import { z } from 'zod';

/**
 * Contratos da autenticação.
 *
 * Os schemas de entrada espelham os do backend — um schema por operação,
 * compartilhado — e os de saída validam a resposta na fronteira: se o contrato
 * mudar, o erro aparece aqui, com o campo exato.
 */

// ─── Entrada ──────────────────────────────────────────────────────────────────

export const credentialsSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, 'Informe o e-mail.')
    .email('Informe um e-mail válido.'),
  password: z.string().min(1, 'Informe a senha.'),
});
export type Credentials = z.infer<typeof credentialsSchema>;

export const forgotPasswordSchema = z.object({
  email: z.string().trim().min(1, 'Informe o e-mail.').email('Informe um e-mail válido.'),
});
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

export const twoFactorCodeSchema = z.object({
  code: z
    .string()
    .trim()
    .regex(/^\d{4}$/, 'O código tem 4 dígitos.'),
});
export type TwoFactorCodeInput = z.infer<typeof twoFactorCodeSchema>;

export type TwoFactorChannel = 'email' | 'phone';

// ─── Saída ────────────────────────────────────────────────────────────────────

export const profileSchema = z.object({
  id: z.string(),
  nome: z.string(),
  email: z.string(),
  perfil: z.string().nullable(),
  isAdmin: z.boolean(),
  somenteLeituraAgenda: z.boolean(),
  foto: z.string().nullable(),
});
export type Profile = z.infer<typeof profileSchema>;

export const authenticatedSchema = z.object({
  status: z.literal('authenticated'),
  token: z.string(),
  expiresIn: z.string(),
  user: profileSchema,
});

export const twoFactorRequiredSchema = z.object({
  status: z.literal('two_factor_required'),
  challengeToken: z.string(),
  channels: z.object({ email: z.string().nullable(), phone: z.string().nullable() }),
});

export const loginOutcomeSchema = z.discriminatedUnion('status', [
  authenticatedSchema,
  twoFactorRequiredSchema,
]);
export type LoginOutcome = z.infer<typeof loginOutcomeSchema>;
export type AuthenticatedOutcome = z.infer<typeof authenticatedSchema>;
export type TwoFactorRequiredOutcome = z.infer<typeof twoFactorRequiredSchema>;

export const twoFactorSentSchema = z.object({
  sentTo: z.string(),
  expiresInSeconds: z.number(),
});
export type TwoFactorSent = z.infer<typeof twoFactorSentSchema>;

export const forgotPasswordResultSchema = z.object({ message: z.string(), token: z.string() });

export const verifyResetCodeResultSchema = z.object({ resetToken: z.string() });

export const newPasswordSchema = z
  .object({
    password: z.string().min(8, 'A senha precisa de ao menos 8 caracteres.').max(200),
    passwordConfirmation: z.string().min(1, 'Confirme a senha.'),
  })
  .refine((data) => data.password === data.passwordConfirmation, {
    path: ['passwordConfirmation'],
    message: 'As senhas não conferem.',
  });
export type NewPasswordInput = z.infer<typeof newPasswordSchema>;

/** Detalhes que acompanham o `401` de credencial inválida. */
export const invalidCredentialsDetailsSchema = z.object({
  attemptsRemaining: z.number().int().nonnegative(),
});

/** Detalhes que acompanham o `429`. */
export const blockedDetailsSchema = z.object({
  retryAfter: z.number().int().nonnegative(),
  blockMinutes: z.number().int().nonnegative(),
});
